<template>
  <div>
    <div class="d-flex flex-wrap align-center mb-2">
      <div class="subtitle-1 font-weight-bold mr-4">AVCs ({{ rows.length }})</div>
      <app-search class="mr-4" style="min-width: 220px" @search="onSearch" />
      <v-chip
        v-for="f in filters"
        :key="f.key"
        small
        class="mr-1 mb-1"
        :color="activeFilter === f.key ? 'primary' : ''"
        :dark="activeFilter === f.key"
        @click="setFilter(f.key)"
      >{{ f.label }} {{ counts[f.key] }}</v-chip>
      <v-spacer />
      <v-btn small outlined color="primary" :disabled="!rows.length" @click="exportCsv">Export CSV</v-btn>
    </div>

    <v-alert v-if="alert" :type="alert.type" dense outlined class="mb-2">
      {{ alert.text }}
      <v-btn v-if="alert.type === 'error'" small text class="ml-2" @click="retryLast">Retry</v-btn>
    </v-alert>

    <div v-if="!rows.length" class="grey--text text--darken-1 pa-4">{{ emptyText }}</div>
    <ag-grid-vue
      v-else
      class="ag-theme-alpine"
      style="width: 100%; height: 50vh"
      :columnDefs="columnDefs"
      :defaultColDef="defaultColDef"
      :rowData="rows"
      :frameworkComponents="frameworkComponents"
      :getRowStyle="getRowStyle"
      :isExternalFilterPresent="isExternalFilterPresent"
      :doesExternalFilterPass="doesExternalFilterPass"
      :enableCellTextSelection="true"
      :ensureDomOrder="true"
      @grid-ready="onGridReady"
    />

    <confirm-action
      :dialog="confirm.open"
      :text="confirm.text"
      :proceed-text="confirm.proceedText"
      cancel-text="Cancel"
      @onBtnClick="onConfirm"
    />
    <type-to-confirm
      v-model="removeOpen"
      :title="`Remove the ${target} copy of ${removing.avcId}`"
      :body="removeBody"
      :consequences="removeConsequences"
      :cvc-name="preflight.cvcName"
      :require-typing="false"
      proceed-label="Remove copy"
      @proceed="removeCopy"
    />
  </div>
</template>

<script>
import "ag-grid-community/dist/styles/ag-grid.css";
import "ag-grid-community/dist/styles/ag-theme-alpine.css";
import { AgGridVue } from "ag-grid-vue";
import Vue from "vue";
import { mapActions } from "vuex";
import {
  GRID_FILTERS,
  TONE_COLOR,
  gridContext,
  badgeFor,
  filterCounts,
  rowMatchesFilter,
  rowTint,
  rowActionItems,
  wholesalerText,
  protocolText,
  newIpText,
  targetCopyText,
  targetCopyTip,
  lastCheckText,
  pendingEventText,
  reconnectedText,
  reconnectVerdict,
  usherStatusText,
  emptyGridText,
  kickOneText,
  kickThisAlert,
  verifyPlan,
  runVerifyLoop,
} from "@/utils/cvcMigrationPage";
import RowActions from "./cvc-migration-row-actions";
import TypeToConfirm from "./type-to-confirm";
import SectionActions from "./section-actions";

// The per-AVC grid: one status word per row, the filters, the quick search,
// CSV export and a row menu (verify, rebuild, repolicy, force remove).
export default {
  name: "CvcMigrationAvcGrid",
  components: { AgGridVue, TypeToConfirm },
  mixins: [SectionActions],
  // The page view owns the kick run; Kick this goes through it.
  inject: ["cvcKickStart"],
  props: {
    preflight: { type: Object, required: true },
    cvcId: { type: Number, required: true },
    targetHostId: { type: Number, required: true },
    lastVerify: { type: Object, default: null },
    // Rows of the reconnection check. Before the shut only rows live on the target set the status.
    reconnectRows: { type: Array, default: null },
    // Optus re-pointed before the shut: no force remove of a live copy.
    earlyRepoint: { type: Boolean, default: false },
    // When Kick all last ran: an offline row is tinted 15 minutes after it.
    kickedAt: { type: String, default: null },
    now: { type: Date, required: true },
  },
  data: () => ({
    activeFilter: "all",
    filters: GRID_FILTERS,
    defaultColDef: { sortable: true, filter: true, resizable: true },
    // ag-Grid builds the row menu itself, so it needs the constructor.
    frameworkComponents: { RowActions: Vue.extend(RowActions) },
    columnDefs: [],
    removeOpen: false,
    removing: { avcId: "", copyId: null },
  }),
  computed: {
    rows () {
      return this.preflight.rows || [];
    },
    phase () {
      return this.preflight.steps ? this.preflight.steps.phase : "none";
    },
    target () {
      return this.preflight.targetHostname;
    },
    source () {
      return this.preflight.sourceHostname;
    },
    // The clock moves the cells only once a minute (the offline tint).
    nowMinute () {
      return new Date(Math.floor(this.now.getTime() / 60000) * 60000);
    },
    ctx () {
      return gridContext(this.rows, this.lastVerify, this.reconnectRows, this.phase, {
        kickedAt: this.kickedAt,
        nowUtc: this.nowMinute,
        record: this.preflight.record,
        poiState: this.preflight.poiState,
      });
    },
    counts () {
      return filterCounts(this.rows, this.ctx);
    },
    emptyText () {
      return emptyGridText(this.target);
    },
    copiesByAvc () {
      const map = {};
      ((this.preflight.record && this.preflight.record.targetCopies) || [])
        .filter(c => !c.removedAt)
        .forEach(c => { map[c.avcId] = c; });
      return map;
    },
    removeBody () {
      return [
        `Remove the copy of ${this.removing.avcId} on ${this.target} (force). It is rebuilt only if you build it again.`,
      ];
    },
    removeConsequences () {
      return [
        `${this.source} is not changed.`,
        `After activation this takes the service down on ${this.target}.`,
      ];
    },
  },
  watch: {
    // Verify results and reconnection rows are not part of the row data: tell
    // the grid to read the cells and the filter again.
    ctx () {
      if (!this.gridApi) return;
      this.gridApi.onFilterChanged();
      this.gridApi.refreshCells({ force: true });
    },
  },
  beforeMount () {
    this.columnDefs = this.buildColumns();
  },
  methods: {
    ...mapActions("cvcMigration", [
      "cvcMigrationBuild",
      "cvcMigrationRepolicy",
      "cvcMigrationVerifyTargetChunk",
      "cvcMigrationRemoveTargetCopies",
    ]),
    buildColumns () {
      const vm = this;
      const badge = (p) => badgeFor(p.data, vm.ctx);
      // Every column reads its cell in a valueGetter: a cell renderer alone
      // never reaches the CSV export.
      return [
        {
          colId: "status",
          headerName: "Status",
          pinned: "left",
          width: 140,
          valueGetter: p => badge(p).label,
          tooltipValueGetter: p => badge(p).reason,
          cellStyle: p => ({ color: TONE_COLOR[badge(p).tone], fontWeight: "bold" }),
        },
        { colId: "avc", headerName: "AVC", width: 150, valueGetter: p => p.data.avcId, tooltipValueGetter: p => p.data.usherAvcId },
        {
          colId: "wholesaler",
          headerName: "Wholesaler",
          width: 160,
          valueGetter: p => wholesalerText(p.data, vm.preflight.wholesalerNames),
          tooltipValueGetter: p => String(p.data.accountNumber),
        },
        { colId: "class", headerName: "Class", width: 80, valueGetter: p => p.data.avcClass },
        {
          colId: "usherStatus",
          headerName: "Usher status",
          width: 160,
          valueGetter: p => usherStatusText(p.data),
          tooltipValueGetter: p => (p.data.inRouterScope ? "" : "Not in router scope"),
        },
        { colId: "speed", headerName: "Speed", width: 80, valueGetter: p => p.data.bandwidthProfile },
        {
          colId: "innerVlan",
          headerName: "Inner VLAN",
          width: 100,
          valueGetter: p => p.data.innerVlan,
          tooltipValueGetter: p => (p.data.localVlanConflictAvcId ? `Held in LOCAL network by ${p.data.localVlanConflictAvcId}` : ""),
          cellStyle: p => (p.data.localVlanConflictAvcId ? { color: TONE_COLOR.red, fontWeight: "bold" } : null),
        },
        { colId: "subif", headerName: "Subif", width: 180, valueGetter: p => p.data.interfaceName },
        {
          colId: "protocol",
          headerName: "Protocol / IP type",
          width: 150,
          valueGetter: p => protocolText(p.data),
          tooltipValueGetter: p => ((p.data.ignoredRadiusServiceIds || []).length
            ? `Ignored RADIUS services: ${p.data.ignoredRadiusServiceIds.join(", ")}`
            : ""),
        },
        {
          colId: "radiusUser",
          headerName: "RADIUS user",
          width: 240,
          valueGetter: p => (p.data.radiusUsername ? `${p.data.radiusUsername} (${p.data.radiusServiceId})` : ""),
        },
        { colId: "currentIp", headerName: "Current IP", width: 130, valueGetter: p => p.data.currentIp || "" },
        { colId: "newIp", headerName: "New IP", width: 170, valueGetter: p => newIpText(p.data) },
        {
          colId: "targetCopy",
          headerName: "Target copy",
          width: 230,
          valueGetter: p => targetCopyText(p.data, vm.copiesByAvc[p.data.avcId], vm.now),
          tooltipValueGetter: p => targetCopyTip(p.data, vm.now),
        },
        {
          colId: "lastCheck",
          headerName: "Last check",
          width: 240,
          valueGetter: p => lastCheckText(vm.ctx.verifyByAvc[p.data.avcId]),
          tooltipValueGetter: p => {
            const d = vm.ctx.verifyByAvc[p.data.avcId];
            return d && d.issues ? d.issues.join("; ") : "";
          },
        },
        { colId: "pendingEvent", headerName: "Pending event", width: 160, valueGetter: p => pendingEventText(p.data) },
        {
          colId: "reconnected",
          headerName: "Reconnected",
          width: 200,
          valueGetter: p => reconnectedText(vm.ctx.reconnectByAvc[p.data.avcId]),
          tooltipValueGetter: p => {
            const r = vm.ctx.reconnectByAvc[p.data.avcId];
            return r ? reconnectVerdict(r, vm.ctx).text : "";
          },
        },
        {
          headerName: "",
          pinned: "right",
          width: 70,
          sortable: false,
          filter: false,
          valueGetter: () => "",
          colId: "actions",
          cellRenderer: "RowActions",
          cellRendererParams: {
            items: row => rowActionItems(row, vm.preflight.steps, vm.preflight.record, { earlyRepoint: vm.earlyRepoint }),
            busy: () => vm.busy,
            onAction: (key, row, item) => vm.onRowAction(key, row, item),
          },
        },
      ];
    },
    onGridReady (params) {
      this.gridApi = params.api;
    },
    onSearch (keyword) {
      if (this.gridApi) this.gridApi.setQuickFilter(keyword);
    },
    setFilter (key) {
      this.activeFilter = key;
      if (this.gridApi) this.gridApi.onFilterChanged();
    },
    isExternalFilterPresent () {
      return this.activeFilter !== "all";
    },
    doesExternalFilterPass (node) {
      return rowMatchesFilter(node.data, this.activeFilter, this.ctx);
    },
    getRowStyle (params) {
      return rowTint(badgeFor(params.data, this.ctx), this.ctx.minutesSinceKick);
    },
    exportCsv () {
      if (!this.gridApi) return;
      // The row menu column is not data.
      const columnKeys = this.columnDefs.filter(c => c.colId !== "actions").map(c => c.colId);
      this.gridApi.exportDataAsCsv({ columnKeys, fileName: `${this.preflight.cvcName}_AVCs.csv` });
    },
    onRowAction (key, row, item) {
      if (key === "verify") this.verifyOne(row);
      if (key === "rebuild") this.askRebuild(row);
      if (key === "repolicy") this.askRepolicy(row);
      if (key === "kick") this.askKick(row);
      if (key === "remove") {
        this.removing = { avcId: row.avcId, copyId: item.copyId };
        this.removeOpen = true;
      }
    },
    verifyOne (row) {
      this.run(`verify-${row.avcId}`, async () => {
        const out = await runVerifyLoop({
          plan: verifyPlan(this.rows, this.preflight.verifyMaxAvcs, [row.avcId]),
          callChunk: avcIds => this.cvcMigrationVerifyTargetChunk({
            cvcId: this.cvcId,
            targetHostId: this.targetHostId,
            avcIds,
          }),
          targetHostname: this.target,
        });
        if (out.results.length) this.$emit("verified", { merged: out.merged, partial: true });
        if (out.stoppedBy === "busy") this.alert = { type: "warning", text: out.message };
        if (out.stoppedBy === "refused") this.alert = { type: "warning", text: out.message };
        if (out.stoppedBy === "error") this.alert = { type: "error", text: out.message };
      });
    },
    askRebuild (row) {
      this.ask(`Rebuild ${row.avcId} on ${this.target}? ${this.source} stays live.`, "Rebuild", () => this.run(`rebuild-${row.avcId}`, () =>
        this.cvcMigrationBuild({ cvcId: this.cvcId, targetHostId: this.targetHostId, avcIds: [row.avcId] })));
    },
    askRepolicy (row) {
      this.ask(`Set the speed policy of ${row.avcId} on ${this.target} again?`, "Repolicy", () => this.run(`repolicy-${row.avcId}`, () =>
        this.cvcMigrationRepolicy({ cvcId: this.cvcId, avcIds: [row.avcId] })));
    },
    askKick (row) {
      this.ask(kickOneText(row, this.target), "Kick", () => this.run(`kick-${row.avcId}`, async () => {
        const result = await this.cvcKickStart({ ids: [row.radiusServiceId], kickAll: false, follow: false });
        this.alert = kickThisAlert(result, row, this.preflight.poiState);
      }));
    },
    removeCopy () {
      const copyId = this.removing.copyId;
      this.run(`remove-${copyId}`, () =>
        this.cvcMigrationRemoveTargetCopies({ cvcId: this.cvcId, copyIds: [copyId], force: true }));
    },
  },
};
</script>
